import {
  LicencePostIn,
  LicenceOut,
  LicenceUpdateIn,
  UserLicencesService,
  Licence,
  OpenAPI,
  ApiError,
} from '@/client'
import {
  deserializeLicenceOut,
  LicenceOutTable,
  serializeLicenceOut,
  createLicenceOutTable,
} from './account.models'
import { db } from '../database'
import {
  _disableAccount,
  _enableAccount,
  _getAccounts,
  _postLicense,
  _patchLicense,
  _deleteLicense,
} from './account.requests'
import { createUpdateSet, deepUnwrap } from '../util'
import { CacheStateRepository, CacheStateTable } from '../cacheState.store'
import dayjs from 'dayjs'
import { type AccountFilters, applyAccountFilters } from './account.filter'
import { editToast, sendToast } from '@/components/sm/SmNotification'
import { useGlobalStore } from '../globalStore'
import { logError } from '@/utils/errors'

const lastCacheChange = ref<Date>()
const loadingFullReload = ref<boolean>(false)

const totalAccountCount = ref<number>()

type Pagination = {
  limit?: number
  offset?: number
}

type Sort = {
  column?: keyof LicenceOutTable
  direction?: 'asc' | 'desc'
}

export const AccountStore = {
  loadingFullReload,
  lastCacheChange,
  totalAccountCount,

  async bulkInsertLicenceOuts(
    licences: LicenceOut[],
    batchSize: number = 1000,
    options?: { setLastCacheChange?: boolean }
  ) {
    const serializedLicences = licences.map(serializeLicenceOut)

    const sharedTextEncoder =
      typeof TextEncoder !== 'undefined' ? new TextEncoder() : undefined

    function getRowBytes(row: LicenceOutTable): number {
      const json = JSON.stringify(row)
      return sharedTextEncoder
        ? sharedTextEncoder.encode(json).length
        : json.length
    }

    await db.transaction().execute(async (trx) => {
      for (let i = 0; i < serializedLicences.length; i += batchSize) {
        const batch = serializedLicences.slice(i, i + batchSize)

        const batchByteSize = batch.reduce(
          (total, row) => total + getRowBytes(row),
          0
        )
        const totalParameters =
          batch.length * (batch[0] ? Object.keys(batch[0]).length : 0)

        console.warn('[AccountStore] bulk insert batch', {
          rows: batch.length,
          batchByteSize,
          totalParameters,
        })

        await trx
          .insertInto('licence_out')
          .values(batch)
          .onConflict((oc) =>
            oc.column('_id').doUpdateSet(createUpdateSet(serializedLicences[0]))
          )
          .execute()
      }
    })
    if (licences.length > 0 && (options?.setLastCacheChange ?? true)) {
      lastCacheChange.value = new Date()
    }
    return licences
  },

  async getAllLicenceOuts(): Promise<LicenceOut[]> {
    const records = await db.selectFrom('licence_out').selectAll().execute()
    return records.map(deserializeLicenceOut)
  },

  async getAccountsCount(
    filters: AccountFilters = { isDeleted: false }
  ): Promise<number> {
    const unwrappedFilters = deepUnwrap(filters)

    let query = db
      .selectFrom('licence_out')
      .select(db.fn.count('_id').as('count'))
    query = applyAccountFilters(query, unwrappedFilters)

    const result = await query.executeTakeFirst()
    return result ? Number(result.count) : 0
  },

  async loadLicensesBatched(
    filter: AccountFilters = { isDeleted: false },
    options: { batchSize: number; sendToast: boolean } = {
      batchSize: 10_000,
      sendToast: true,
    }
  ) {
    const totalCount: number =
      await UserLicencesService.countLicencesApiV1SoftwareLicencesCountGet({
        softwareId: filter.applicationId,
        subAccountId: filter.subAccountId,
        status: filter.status ? filter.status[0] : undefined, // WARNING: This is not what we want
        updatedGte: filter.updatedAfter,
        updatedLt: filter.updatedBefore,
        deletedAtGte: filter.deletedAfter,
        includeDeleted: filter.isDeleted,
      })

    const totalBatches = Math.ceil(totalCount / options.batchSize)
    let completedBatches = 0
    const toastId = sendToast(
      `Loading Accounts (0/${totalBatches})`,
      undefined,
      'loading'
    )

    const batchPromises: Promise<void | Array<Licence>>[] = []

    for (let i = 0; i < totalCount; i += options.batchSize) {
      const promise = UserLicencesService.getLicencesApiV1SoftwareLicencesGet({
        softwareId: filter.applicationId,
        subAccountId: filter.subAccountId,
        status: filter.status ? filter.status[0] : undefined,
        updatedGte: filter.updatedAfter,
        updatedLt: filter.updatedBefore,
        deletedAtGte: filter.deletedAfter,
        includeDeleted: filter.isDeleted,
        limit: options.batchSize,
        offset: i,
      }).then(async (response) => {
        // Aktualisierung des Fortschritts
        completedBatches++
        await this.bulkInsertLicenceOuts(response, undefined, {
          setLastCacheChange: false,
        })
        editToast(toastId, {
          title: `Loading Accounts (${completedBatches}/${totalBatches})`,
          type: 'loading',
        })
      })
      batchPromises.push(promise)
    }

    await Promise.all(batchPromises)
    editToast(toastId, { title: 'Accounts Loaded', type: 'success' })
    lastCacheChange.value = new Date()
  },

  async deleteAllLicenceOuts(): Promise<void> {
    await db.deleteFrom('licence_out').execute()
    lastCacheChange.value = new Date()
  },

  getDatabase() {
    return db.selectFrom('licence_out')
  },

  async refreshAccountCacheFromApi(
    filter: AccountFilters = {},
    useServerCache: boolean = true,
    includeDeleted: boolean = false
  ) {
    const results = await _getAccounts({
      softwareId: filter.applicationId,
      subAccountId: filter.subAccountId,
      status: filter.status ? filter.status[0] : undefined, // WARNING: This is not what we want
      updatedGte: filter.updatedAfter,
      updatedLt: filter.updatedBefore,
      deletedAtGte: filter.deletedAfter,
      cached: useServerCache,
      includeDeleted: includeDeleted,
    })

    if (!results.error && results.data) {
      await this.bulkInsertLicenceOuts(results.data)
    }
    return results
  },

  async updateAccountCacheFromApi(fullReload: boolean = false) {
    let isFullReload = true

    const lastRefresh = await CacheStateRepository.getCacheStateById('accounts')

    const filter: AccountFilters = {}
    if (lastRefresh && lastRefresh.lastUpdate && !fullReload) {
      filter.updatedAfter = lastRefresh.lastUpdate
      isFullReload = false
    }

    if (isFullReload) {
      const globalStore = useGlobalStore()

      if (!loadingFullReload.value) {
        loadingFullReload.value = true
        if (globalStore.state.useAccountStreaming) {
          await this.loadLicensesStreamed()
        } else {
          await this.loadLicensesBatched()
        }
      }
    } else {
      // Update updated licenses
      await this.refreshAccountCacheFromApi(filter)

      // Update deleted licenses
      if (lastRefresh && lastRefresh.lastUpdate && !fullReload) {
        await this.refreshAccountCacheFromApi(
          {
            deletedAfter: lastRefresh?.lastUpdate,
          },
          true, // use serverSide Cache
          true // includeDeleted
        )
      }
    }

    type UpdateCacheStateTable = Partial<Omit<CacheStateTable, 'id'>> & {
      id: string
    }
    const update: UpdateCacheStateTable = {
      id: 'accounts',
      lastUpdate: dayjs().toISOString(),
    }

    if (isFullReload) {
      update.lastCompleteLoad = dayjs().toISOString()
    }

    await CacheStateRepository.upsertCacheState(update)
    loadingFullReload.value = false
  },

  async loadLicensesStreamed() {
    const controller = new AbortController()
    const toastID = sendToast(
      'Loading Accounts',
      undefined,
      'loading',
      undefined,
      true,
      0,
      100000
    )

    let loadedCount = 0
    let rows: LicenceOut[] = []

    try {
      const res = await fetch(
        `${OpenAPI.BASE}/api/v1/software/licences/stream`,
        {
          headers: {
            Accept: 'application/x-ndjson',
            authorization: `Bearer ${OpenAPI.TOKEN}`,
          },
          signal: controller.signal,
        }
      )
      if (!res.ok || !res.body) throw new Error('Stream failed')

      const reader = res.body.getReader()
      const decoder = new TextDecoder() // Browser entpackt gzip automatisch
      let buf = ''

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })

        // Zeilenweise parsen
        let idx
        while ((idx = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, idx).trim()
          buf = buf.slice(idx + 1)
          if (!line) continue
          try {
            rows.push(JSON.parse(line))

            // Log progress every 1000 items
            if (rows.length % 1000 === 0) {
              await this.bulkInsertLicenceOuts(rows)
              loadedCount += 1000
              editToast(toastID, {
                currentProgress: loadedCount,
                totalProgress: totalAccountCount.value,
              })
              rows = []
            }
          } catch (e) {
            console.error('Failed to parse line:', e)
          }
        }
      }
      await this.bulkInsertLicenceOuts(rows)
      editToast(toastID, {
        type: 'success',
        duration: 1000,
        currentProgress: totalAccountCount.value,
      })
    } catch (e) {
      console.error('Stream error:', e)
    }
  },

  async getAccounts(
    filters: AccountFilters = { isDeleted: false },
    pagination: Pagination = {},
    sort: Sort = {}
  ): Promise<LicenceOut[]> {
    let query = db.selectFrom('licence_out').selectAll()

    query = applyAccountFilters(query, filters)

    if (pagination.limit) {
      query = query.limit(pagination.limit)
    }
    if (pagination.offset) {
      query = query.offset(pagination.offset)
    }
    if (sort.column) {
      query = query.orderBy(sort.column, sort.direction ?? 'asc')
    }

    const results = await query.execute()
    return results.map(deserializeLicenceOut)
  },

  async createAccount(newAccount: LicencePostIn) {
    return _postLicense(newAccount).then(async (response) => {
      if (!response.error && response.data) {
        const serializedLicence = serializeLicenceOut(response.data)
        await db.insertInto('licence_out').values(serializedLicence).execute()
      }
      return response
    })
  },

  async deactivateAccount(id: string) {
    return _disableAccount(id).then(async (response) => {
      if (!response.error && response.data) {
        await this.bulkInsertLicenceOuts([response.data])
      }
      return response
    })
  },

  async activateAccount(id: string) {
    return _enableAccount(id).then(async (response) => {
      if (!response.error && response.data) {
        await this.bulkInsertLicenceOuts([response.data])
      }
      return response
    })
  },

  async getAccountById(id: string): Promise<LicenceOut | null> {
    const record = await db
      .selectFrom('licence_out')
      .selectAll()
      .where('licence_out._id', '=', id)
      .executeTakeFirst()

    if (!record) {
      logError(
        null,
        `Account with ID ${id} not found in sqlite cache`,
        { id },
        { op: 'sqlite.getAccountById' }
      )
    }

    let result = record ? deserializeLicenceOut(record) : record

    if (!record) {
      try {
        const response =
          await UserLicencesService.getLicenceApiV1SoftwareLicencesLicenceIdGet(
            {
              includeDeleted: true,
              licenceId: id,
            }
          )

        if (response) {
          result = response
          this.bulkInsertLicenceOuts([response])
        } else {
          logError(
            null,
            `Account with ID ${id} not found by API`,
            { id },
            { op: 'api.getLicenceById' }
          )
        }
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          logError(
            error,
            `Account with ID ${id} returned 404 when fetched from API`,
            { id },
            { op: 'api.getLicenceById' }
          )
        } else {
          throw logError(
            error,
            `Failed to fetch account with ID ${id} from API`,
            { id },
            { op: 'api.getLicenceById' }
          )
        }
      }
    }

    return result || null
  },

  async updateAccount(
    id: string,
    updates: LicenceUpdateIn,
    toastOptions: {
      sendToast?: boolean
      loadingToastTitle?: string
      successToastTitle?: string
    } = {}
  ) {
    return await _patchLicense(id, updates, toastOptions).then(
      async (response) => {
        if (response.error) return response

        const serializedUpdates = serializeLicenceOut({
          ...response.data,
          _id: id,
        } as LicenceOut)

        await db
          .updateTable('licence_out')
          .set(serializedUpdates)
          .where('licence_out._id', '=', id)
          .execute()

        lastCacheChange.value = new Date()
        return response
      }
    )
  },

  async deleteAccount(id: string) {
    return _deleteLicense(id).then(async (response) => {
      await db
        .deleteFrom('licence_out')
        .where('licence_out._id', '=', id)
        .execute()
      lastCacheChange.value = new Date()
      return response
    })
  },
  useAccountsCount(initialFilters: AccountFilters | Ref<AccountFilters> = {}) {
    const count = ref<number>(0)

    const fetchCount = async () => {
      const filters = deepUnwrap(initialFilters) as AccountFilters
      count.value = await this.getAccountsCount(filters)
    }

    const stopWatch = watch(
      [() => unref(initialFilters), lastCacheChange],
      async () => {
        await fetchCount()
      },
      { deep: true }
    )

    fetchCount()

    return {
      count,
      unsubscribe: stopWatch,
      lastCacheUpdate: lastCacheChange,
    }
  },

  useAccounts(
    initialFilters:
      | AccountFilters
      | Ref<AccountFilters>
      | ComputedRef<AccountFilters> = {},
    initialPagination:
      | Pagination
      | Ref<Pagination>
      | ComputedRef<Pagination> = {},
    initialSort: Sort | Ref<Sort> | ComputedRef<Sort> = {}
  ) {
    const data = ref<LicenceOut[]>([])
    const isLoading = ref(true)

    const fetchData = async () => {
      const filters = deepUnwrap(initialFilters) as AccountFilters
      const pagination = deepUnwrap(initialPagination) as Pagination
      const sort = deepUnwrap(initialSort) as Sort
      data.value = await this.getAccounts(filters, pagination, sort)

      isLoading.value = false
    }

    const stopWatch = watch(
      [
        () => unref(initialFilters),
        () => unref(initialPagination),
        () => unref(initialSort),
        lastCacheChange,
      ],
      async () => {
        await fetchData()
      },
      { deep: true }
    )

    const updateParameters = (
      newFilters: Partial<AccountFilters> = {},
      newPagination: Partial<Pagination> = {},
      newSort: Partial<Sort> = {}
    ) => {
      const cloneAndMerge = <T extends object>(
        source: T | Ref<T>,
        updates: Partial<T>
      ): T => {
        if (isRef(source)) {
          return { ...source.value, ...updates }
        } else {
          return { ...source, ...updates }
        }
      }

      initialFilters = cloneAndMerge(initialFilters, newFilters)
      initialPagination = cloneAndMerge(initialPagination, newPagination)
      initialSort = cloneAndMerge(initialSort, newSort)

      fetchData()
    }

    fetchData()

    return {
      data,
      unsubscribe: stopWatch,
      updateParameters,
      loading: isLoading,
      lastCacheUpdate: lastCacheChange,
    }
  },

  async reset() {
    await db.schema.dropTable('licence_out').ifExists().execute()
    await createLicenceOutTable()
    lastCacheChange.value = undefined
  },
}
